import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLinkIcon, EyeIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EpkEditorCard } from '../components/website/EpkEditorCard';
import { PublishBar } from '../components/website/PublishBar';
import { describeDraftChanges, discardWebsiteDraft, getDraftWebsiteContent, getLastPublishedAt, hasUnpublishedChanges, publishWebsiteContent, saveDraftWebsiteContent, type WebsiteContent } from '../data/website';

/**
 * The press kit control panel. It shares the same draft-and-publish pipeline as
 * Website Studio, but lives in its own section because the audience is bookers
 * and press rather than fans.
 */
export function PressKit() {
  const [draft, setDraft] = useState<WebsiteContent>(() => getDraftWebsiteContent());
  const [lastPublishedAt, setLastPublishedAt] = useState<string | null>(() => getLastPublishedAt());
  const [justPublished, setJustPublished] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!justPublished) return undefined;
    const timer = window.setTimeout(() => setJustPublished(false), 2400);
    return () => window.clearTimeout(timer);
  }, [justPublished]);
  const update = useCallback(<Key extends keyof WebsiteContent,>(key: Key, value: WebsiteContent[Key]) => {
    setDraft(current => {
      const next = {
        ...current,
        [key]: value
      };
      setError(saveDraftWebsiteContent(next) ? '' : 'Couldn’t save this change on this device. Check Website Studio’s cloud status and try again.');
      return next;
    });
  }, []);
  const publish = () => {
    if (!publishWebsiteContent(draft)) {
      setError('Couldn’t publish from this device. Check Website Studio’s cloud status and try again.');
      return;
    }
    setLastPublishedAt(getLastPublishedAt());
    setJustPublished(true);
    setError('');
  };
  return <div data-id="element-1703" data-mp="1mkcl8yxvo14k">
      <PageHeader eyebrow="Press Kit" title="Everything a booker needs, on one page." subtitle="Published at /epk. It is not linked from the public site and stays out of search — share the address directly with venues, promoters, and press." actions={<a href="/epk" target="_blank" rel="noreferrer" data-id="element-1705" data-mp="yvtdqlmnjsdj">
            <Button variant="secondary" size="sm" data-id="element-1706" data-mp="yt6wfu9ylyi4">
              View live press kit <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-1707" data-mp="12xof1q4l25rb" />
            </Button>
          </a>} data-id="element-1704" data-mp="1mk2tp6x6plwr" />

      <div className="mb-4" data-id="element-1708" data-mp="17ueuqcch6zc7">
        <PublishBar hasChanges={hasUnpublishedChanges(draft)} changedSections={describeDraftChanges(draft)} lastPublishedAt={lastPublishedAt} justPublished={justPublished} onPublish={publish} onDiscard={() => {
        setDraft(discardWebsiteDraft());
        setError('');
      }} data-id="element-1709" data-mp="17u50gckee60p" />
      </div>

      {error && <div role="alert" className="mb-4 rounded-xl border border-danger/25 bg-danger/10 px-3 py-2.5 text-[12px] text-rose-200" data-id="element-1710" data-mp="cki7lh7dhgso">
          {error}
        </div>}

      <Card className="mb-4 flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-1711" data-mp="2n3tvpcu3642">
        <div data-id="element-1712" data-mp="3ury0hvps2xe2">
          <h3 className="text-[13.5px] font-semibold text-white" data-id="element-1713" data-mp="3uuk2lftwgkow">Check it before you send it</h3>
          <p className="mt-1 max-w-lg text-[12px] leading-relaxed text-zinc-400" data-id="element-1714" data-mp="3pvh98sna82wr">
            Opens the press kit exactly as a booker will see it, built from your draft.
          </p>
        </div>
        <Link to="/studio/preview?page=epk" className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#d97742]/15 px-3 py-2 text-[12.5px] font-medium text-[#e8a369] transition-colors duration-150 ease-out hover:bg-[#d97742]/25" data-id="element-1715" data-mp="3g1ib04qbx3a3">
          <EyeIcon className="h-3.5 w-3.5" data-id="element-1716" data-mp="3254t4uhvuzv3" /> Preview press kit
        </Link>
      </Card>

      <Card className="p-5" data-id="element-1717" data-mp="161qbpr9qacfd">
        <EpkEditorCard draft={draft} update={update} data-id="element-1718" data-mp="161gvgvehjjmv" />
      </Card>
    </div>;
}